import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconCheck, IconAlertCircle, IconArrowNarrowRight } from '@tabler/icons-react';
import { useAuth } from '@monorepo/shared-auth';
import { getInvitation, acceptInvitation } from '../../services/invitationService';
import { addLeagueMember } from '../../services/leagueService';

const LeagueInvite = () => {
  const { token } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const result = await getInvitation(token);

        if (result.error) {
          setError(result.error);
        } else {
          setInvitation(result.invitation);
        }
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('Failed to load invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  // Auto-accept if user is logged in and invitation is valid
  useEffect(() => {
    const autoAccept = async () => {
      if (!currentUser || !invitation || accepting || accepted) return;

      // Check if the logged-in user's email matches the invitation
      const userEmail = currentUser.email?.toLowerCase();
      const inviteEmail = invitation.inviteeEmail?.toLowerCase();

      if (userEmail !== inviteEmail) {
        setError(`This invitation was sent to ${invitation.inviteeEmail}. Please log in with that email address.`);
        return;
      }

      // Auto-accept the invitation
      await handleAcceptInvitation();
    };

    autoAccept();
  }, [currentUser, invitation]);

  const handleAcceptInvitation = async () => {
    if (!currentUser) {
      // Redirect to signup with return URL
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`/signup?redirect=${returnUrl}&email=${encodeURIComponent(invitation.inviteeEmail)}`);
      return;
    }

    setAccepting(true);
    setError('');

    try {
      // Add user to league with team information if applicable
      await addLeagueMember(invitation.leagueId, currentUser.uid, {
        role: invitation.role || 'player',
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        email: currentUser.email || '',
        teamName: invitation.teamName || null,
        teamCaptainId: invitation.role === 'captain' ? currentUser.uid : null,
        status: 'active'
      });

      // Mark invitation as accepted
      await acceptInvitation(token, currentUser.uid);

      setAccepted(true);

      // Redirect to league page after 2 seconds
      setTimeout(() => {
        navigate(`/leagues/${invitation.leagueId}`);
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation. You may already be a member of this league.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <main className="ub-league-invite-page">
        <div className="ub-invite-container">
          <div className="ub-loading-spinner">
            <div className="ub-spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="ub-league-invite-page">
        <div className="ub-invite-container">
          <div className="ub-invite-card ub-error-card">
            <div className="ub-icon-wrapper ub-error-icon">
              <IconAlertCircle size={64} />
            </div>
            <h1>Invitation Error</h1>
            <p className="ub-error-text">{error}</p>
            <button
              onClick={() => navigate('/leagues')}
              className="ub-btn-primary"
            >
              Browse Leagues
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (accepted) {
    return (
      <main className="ub-league-invite-page">
        <div className="ub-invite-container">
          <div className="ub-invite-card ub-success-card">
            <div className="ub-icon-wrapper ub-success-icon">
              <IconCheck size={64} />
            </div>
            <h1>Welcome to the League!</h1>
            <p className="ub-success-text">
              You've successfully joined <strong>{invitation.leagueName}</strong>
              {invitation.role === 'captain' && invitation.teamName && (
                <> as captain of <strong>{invitation.teamName}</strong></>
              )}
              .
            </p>
            <p className="ub-redirect-text">Redirecting you to the league page...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="ub-league-invite-page">
      <div className="ub-invite-container">
        <div className="ub-invite-card">
          <div className="ub-invite-header">
            <h1>You're Invited!</h1>
            <p className="ub-invite-subtitle">
              <strong>{invitation.inviterName}</strong> has invited you to join their league
            </p>
          </div>

          <div className="ub-league-details">
            <div className="ub-detail-item">
              <span className="ub-detail-label">League:</span>
              <span className="ub-detail-value">{invitation.leagueName}</span>
            </div>
            <div className="ub-detail-item">
              <span className="ub-detail-label">Sport:</span>
              <span className="ub-detail-value">{invitation.sport}</span>
            </div>
            <div className="ub-detail-item">
              <span className="ub-detail-label">Invited by:</span>
              <span className="ub-detail-value">{invitation.inviterName}</span>
            </div>
            {invitation.role === 'captain' && (
              <div className="ub-detail-item">
                <span className="ub-detail-label">Role:</span>
                <span className="ub-detail-value">Team Captain</span>
              </div>
            )}
            {invitation.teamName && (
              <div className="ub-detail-item">
                <span className="ub-detail-label">Team:</span>
                <span className="ub-detail-value">{invitation.teamName}</span>
              </div>
            )}
          </div>

          {!currentUser && (
            <div className="ub-auth-notice">
              <p>
                To join this league, you'll need to {' '}
                <strong>create an account</strong> or <strong>sign in</strong>.
              </p>
            </div>
          )}

          <button
            onClick={handleAcceptInvitation}
            disabled={accepting}
            className="ub-btn-accept"
          >
            {accepting ? (
              'Joining League...'
            ) : currentUser ? (
              <>
                Join League <IconArrowNarrowRight size={20} />
              </>
            ) : (
              <>
                Create Account & Join <IconArrowNarrowRight size={20} />
              </>
            )}
          </button>

          {!currentUser && (
            <p className="ub-signin-link">
              Already have an account?{' '}
              <button
                onClick={() => {
                  const returnUrl = encodeURIComponent(window.location.pathname);
                  navigate(`/signin?redirect=${returnUrl}`);
                }}
                className="ub-link-btn"
              >
                Sign in here
              </button>
            </p>
          )}
        </div>
      </div>
    </main>
  );
};

export default LeagueInvite;
